import React, { useState, SyntheticEvent } from 'react';
import * as Styled from './uploadModalStyles';
import { Button } from '@common/components/Button/Button';
import { ButtonTypes } from '@common/components/Button/types';
import { DragAndDropFileInput, FileItem } from '@common/components';
import theme from '@common/theme';
import { UNITS_DIVIDER } from '@common/constants';

interface IUploadContactListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (file: File | null) => void;
  isUploading?: boolean;
}

export const UploadContactListModal = ({ isOpen, onClose, onConfirm, isUploading }: IUploadContactListModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  if (!isOpen) return null;

  const handleFileSubmit = (files: FileList | null, event: SyntheticEvent) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file type (CSV only)
    if (!file.name.toLowerCase().endsWith('.csv')) {
      // File validation will be handled by parent component
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const handleConfirm = () => {
    onConfirm(selectedFile);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const fileSize = selectedFile ? Math.round(selectedFile.size / UNITS_DIVIDER) : 0;

  return (
    <Styled.ModalOverlay onClick={handleOverlayClick}>
      <Styled.ModalContainer>
        <Styled.ModalHeader>
          <Styled.HeaderText>Upload contact list</Styled.HeaderText>
          <Styled.SubheadingText>.csv upload</Styled.SubheadingText>
        </Styled.ModalHeader>

        <Styled.ModalBody>
          <Styled.FileUploadSection>
            <DragAndDropFileInput
              onFileSubmit={handleFileSubmit}
              subText="Accepts .csv files only"
              disabled={isUploading}
              accept=".csv"
            />
            {selectedFile && (
              <Styled.FileItemWrapper>
                <FileItem
                  fileName={selectedFile.name}
                  fileSize={`${fileSize}KB`}
                  onClick={handleRemoveFile}
                  isLoading={isUploading}
                />
              </Styled.FileItemWrapper>
            )}
          </Styled.FileUploadSection>

          <Styled.ConsentSection>
            <Styled.ConsentText>
              By uploading this file, you confirm all contacts listed have consented to receive Broadcast emails.
            </Styled.ConsentText>
          </Styled.ConsentSection>
        </Styled.ModalBody>

        <Styled.ModalFooter>
          <Button type={ButtonTypes.TEXT} text="Cancel" onClick={handleClose} disabled={isUploading} />
          <Button
            type={ButtonTypes.PRIMARY}
            text="Confirm"
            onClick={handleConfirm}
            disabled={!selectedFile || isUploading}
            color={theme.colors.mediumGreen}
          />
        </Styled.ModalFooter>
      </Styled.ModalContainer>
    </Styled.ModalOverlay>
  );
};
